import { ValidationErrors } from '@angular/forms';
import { createSelector, Store } from '@ngxs/store';
import { AddMessage } from '@records/state/message-center/add-message.action';
import { MessageCenterState } from '@records/state/message-center/message-center-state';
import {
  MessageCenterStateModel,
  MessageItemLevel,
  MessageItemType,
} from '@records/state/message-center/message-center.model';
import { RemoveMessage } from '@records/state/message-center/remove-message.action';

const allMessages = createSelector([MessageCenterState], (messages: MessageCenterStateModel) => messages);

export class ErrorMessengerService {
  /**
   * Captures errors on each validation run
   */
  private _errorsSnapshot: { [key: string]: string } = {};

  // shorter fn to access the codes
  private get errorKeys() {
    return Object.keys(this._errorsSnapshot || {});
  }

  get #messageType() {
    switch (this.type) {
      case MessageItemType.warning:
        return AddMessage.Warning;
      default:
        return AddMessage.Error;
    }
  }

  constructor(
    private store: Store,
    private level: MessageItemLevel,
    private id: number,
    private type: MessageItemType
  ) {}

  /**
   * Clears accumulated errors for input
   */
  public clearErrors(errors: ValidationErrors) {
    this.#dispatchIfMatching(RemoveMessage.ByTypeLevelAndId(this.type, this.level, this.id));
    this._errorsSnapshot = errors;
  }

  public registerErrors(errors: ValidationErrors) {
    // Refresh the errors for current id and then re-register errors
    this.#dispatchIfMatching(RemoveMessage.ByLevelAndId(this.level, this.id));
    this._errorsSnapshot = errors;
    this.errorKeys.forEach(errorCode => {
      const params = this._errorsSnapshot[errorCode];
      this.store.dispatch(this.#messageType(this.level, this.id, errorCode, params));
    });
  }

  /**
   * Skips dispatching a RemoveMessage that matches nothing. The criteria come from the action itself
   * (RemoveMessage.matches), so this pre-check can never diverge from what the action removes.
   */
  #dispatchIfMatching(action: RemoveMessage) {
    const messages = this.store.selectSnapshot(allMessages);
    // state not readable as a message list: dispatch as before rather than risk keeping a stale message
    if (!Array.isArray(messages) || messages.some(item => action.matches(item))) {
      this.store.dispatch(action);
    }
  }
}
